import React from 'react'
import {
  Icon,
  MenuItem,
  Avatar,
  Hidden,
  IconButton,
  useMediaQuery,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import useAuth from 'app/hooks/useAuth'
import useSettings from 'app/hooks/useSettings'
import { authOperations } from 'app/ducks/auth'
import { useDispatch } from 'react-redux'
import MatxMenu from 'app/components/MatxMenu/MatxMenu'
import { NavLink, useHistory } from 'react-router-dom'
import useIsMobile from 'app/hooks/useIsMobile'
import useFeatureFlag from 'app/ducks/featureFlags/hooks'
import { useSectionNav } from 'app/hooks/useSectionNav'
import { SECTION_NAV_FLAG } from 'app/navigation/sectionNavFlag'
import useCanSetUpMcp from 'app/views/mcp/useCanSetUpMcp'
import { MCP_SETUP_PATH } from 'app/views/mcp/mcp'
import InternalCampaignsButton from 'app/components/InternalCampaigns/InternalCampaignsButton'

const useStyles = makeStyles(({ palette, ...theme }) => ({
  topbar: {
    top: 0,
    zIndex: 96,
    transition: 'all 0.3s ease',
    background:
      'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 44%, rgba(247, 247, 247, 0.4) 50%, rgba(255, 255, 255, 0))',

    '& .topbar-hold': {
      backgroundColor: palette.primary.contrastText,
      height: 80,
      paddingLeft: 18,
      paddingRight: 20,
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 16,
        paddingRight: 16,
      },
      [theme.breakpoints.down('xs')]: {
        paddingLeft: 14,
        paddingRight: 16,
      },
    },
    '& .fixed': {
      boxShadow: theme.shadows[8],
      height: 64,
    },
  },
  userMenu: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    borderRadius: 24,
    padding: 4,
    '& span': {
      margin: '0 8px',
      // color: palette.text.secondary
    },
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 185,
  },
  sectionIcon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.action.selected,
    color: palette.primary.main,
    flexShrink: 0,
  },
  sectionIconCompact: {
    width: 28,
    height: 28,
  },
  sectionTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  sectionTitleCompact: {
    fontSize: 16,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tabsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 20px',
    overflowX: 'auto',
    borderTop: `1px solid ${palette.divider}`,
    backgroundColor: palette.primary.contrastText,
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    padding: '0 16px',
    borderRadius: 16,
    fontSize: 13,
    whiteSpace: 'nowrap',
    color: palette.text.secondary,
    '&:hover': {
      backgroundColor: palette.action.hover,
    },
  },
  tabExternalIcon: {
    marginLeft: 6,
    '&.MuiIcon-root': {
      fontSize: 15,
    },
  },
  tabActive: {
    color: palette.primary.contrastText,
    backgroundColor: palette.primary.main,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: palette.primary.main,
    },
  },
}))

// Behind the `new_navigation` feature flag (see docs/FEATURE_FLAGS.md):
// off, this is the original bare topbar (sidebar toggle, back button,
// user menu) unchanged, on both desktop and mobile. On:
// - Desktop gets the active section's icon/title and a tab row for
//   the section's pages.
// - Mobile gets a single compact header row instead: just the section
//   icon/title, no tab row — the section's pages (submenus) are a
//   dropdown on that section's row in MobileSectionSheet (the main
//   menu) instead of living in this top-level nav.
// Page-specific action buttons stay wherever each page renders them
// (they don't relocate into the topbar).
const Layout1Topbar = () => {
  const sectionNavEnabled = useFeatureFlag(SECTION_NAV_FLAG)
  const classes = useStyles()
  const history = useHistory()
  const canSetUpMcp = useCanSetUpMcp()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const dispatch = useDispatch()
  const { userInfo } = useAuth()
  const { activeSection } = useSectionNav()

  const { settings, updateSettings } = useSettings()
  const fixed = settings?.layoutSettings?.topbar?.fixed
  const isMdScreen = useMediaQuery(theme.breakpoints?.down('md'))

  const isMobileSection = sectionNavEnabled && isMobile

  const updateSidebarMode = (sidebarSettings) => {
    updateSettings({
      layoutSettings: {
        leftSidebar: {
          ...sidebarSettings,
        },
      },
    })
  }

  const handleSidebarToggle = () => {
    let { layoutSettings } = settings
    let mode

    if (isMdScreen) {
      mode = layoutSettings.leftSidebar.mode === 'close' ? 'mobile' : 'close'
    } else {
      mode = layoutSettings.leftSidebar.mode === 'full' ? 'close' : 'full'
    }

    updateSidebarMode({ mode })
  }

  // On mobile, submenus live in a dropdown on each section's row in
  // the main menu (MobileSectionSheet) instead of this tab row — see
  // that component. External links (e.g. the Django admin) render as
  // tabs too, opening in a new browser tab like in the legacy sidenav.
  const tabs =
    sectionNavEnabled && !isMobileSection ? activeSection?.children ?? [] : []

  return (
    <div className={classes.topbar}>
      <div
        className={clsx({ 'topbar-hold': true, fixed: fixed })}
        style={isMobileSection ? { height: 56 } : undefined}
      >
        <div className="flex justify-between items-center h-full">
          <div className="flex items-center" style={{ minWidth: 0 }}>
            {!isMobile && (
              <IconButton
                className="hide-on-pc"
                color="primary"
                onClick={handleSidebarToggle}
              >
                <Icon>menu</Icon>
              </IconButton>
            )}
            {!!isMobile && !!theme.logoSymbolTopNav && !sectionNavEnabled && (
              <img
                src={theme.logoSymbolTopNav}
                className={classes.logo}
                height="40px"
                style={{ margin: '4px' }}
                alt=""
              />
            )}
            {history.action === 'PUSH' && (
              <IconButton color="primary" onClick={() => history.goBack()}>
                <Icon>chevron_left</Icon>
              </IconButton>
            )}

            {sectionNavEnabled && !!activeSection && (
              <div
                className="flex items-center"
                style={{
                  gap: isMobileSection ? 10 : 12,
                  marginLeft: 4,
                  minWidth: 0,
                }}
              >
                <div
                  className={clsx(classes.sectionIcon, {
                    [classes.sectionIconCompact]: isMobileSection,
                  })}
                >
                  <Icon fontSize="small">{activeSection.icon}</Icon>
                </div>
                <h1
                  className={clsx(classes.sectionTitle, {
                    [classes.sectionTitleCompact]: isMobileSection,
                  })}
                >
                  {activeSection.name}
                </h1>
              </div>
            )}
          </div>
          <div className="flex items-center">
            <InternalCampaignsButton />
            <MatxMenu
              menuButton={
                <div className={[classes.userMenu, 't-user-menu'].join(' ')}>
                  <Hidden xsDown>
                    <span>
                      Olá, <strong>{userInfo?.first_name}</strong>
                    </span>
                  </Hidden>
                  <Avatar className="cursor-pointer" src={userInfo?.picture} />
                </div>
              }
            >
              {!isMobile && (
                <MenuItem
                  onClick={() => history.push('/personal-data')}
                  className={[classes.menuItem, 't-nav-personal-info'].join(
                    ' '
                  )}
                >
                  <Icon> candlestick_chart </Icon>
                  <span className="pl-4">Informações pessoais</span>
                </MenuItem>
              )}
              {canSetUpMcp && (
                <MenuItem
                  onClick={() => history.push(MCP_SETUP_PATH)}
                  className={[classes.menuItem, 't-nav-mcp-setup'].join(' ')}
                >
                  <Icon> smart_toy </Icon>
                  <span className="pl-4">Conectar assistente de IA</span>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => dispatch(authOperations.logout())}
                className={classes.menuItem}
              >
                <Icon> power_settings_new </Icon>
                <span className="pl-4">Sair</span>
              </MenuItem>
            </MatxMenu>
          </div>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className={classes.tabsRow}>
          {tabs.map((tab) =>
            tab.type === 'extLink' ? (
              <a
                key={tab.path}
                href={tab.path}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.tab}
              >
                {tab.name}
                <Icon className={classes.tabExternalIcon}>open_in_new</Icon>
              </a>
            ) : (
              <NavLink
                key={tab.path}
                to={tab.path}
                exact={tab.exact}
                className={classes.tab}
                activeClassName={classes.tabActive}
              >
                {tab.name}
              </NavLink>
            )
          )}
        </div>
      )}
    </div>
  )
}

export default Layout1Topbar
